import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Link} from 'react-router-dom'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {DataTable, type Column} from '@/components/data-table'
import {InvitarExternoDialog} from '@/components/invitar-externo-dialog'
import {KairosMark} from '@/components/kairos-mark'
import {HelpIcon} from '@/components/help-icon'
import {ListEmptyState} from '@/components/section-card'
import {LicenciaBadge} from '@/components/licencia-badge'
import {useEmployees, type Employee} from '@/hooks/empresa/identidad/use-employees'

/** Los externos con acceso no cuentan como trabajadores (capítulo de estándares ni banda), pero
 * firman. Dar acceso no designa a nadie (Res. 0312/2019, estándar 1.1.1). */
export function ExternosAccesoSection({businessId, canManage}: {businessId: string; canManage: boolean}) {
  const {t} = useTranslation()
  const [creating, setCreating] = useState(false)
  const {data} = useEmployees(businessId, {
    page: 1,
    search: '',
    sortBy: 'name',
    sortDir: 'asc',
    vinculo: 'externo',
  })
  const externos = data?.items ?? []

  const columns: Column<Employee>[] = [
    {
      key: 'name',
      header: t('externosAcceso.table.name'),
      width: 'w-44',
      cell: (e) => (
        <Link
          to={`/businesses/${businessId}/employees/${e.id}`}
          className="block truncate font-medium text-foreground hover:underline">
          {e.name} {e.last_name}
        </Link>
      ),
    },
    {
      key: 'cedula',
      header: t('externosAcceso.table.cedula'),
      width: 'w-28',
      cell: (e) => <span className="block truncate font-mono text-xs text-muted-foreground">{e.cedula}</span>,
    },
    {
      key: 'role',
      header: t('externosAcceso.table.role'),
      width: 'w-36',
      cell: (e) => <Tag tone="neutral">{t(`roles.${e.role}`)}</Tag>,
    },
    {
      key: 'licencia',
      header: t('externosAcceso.table.licencia'),
      width: 'w-32',
      cell: (e) =>
        e.licencia_estado ? (
          <LicenciaBadge estado={e.licencia_estado} categoria={e.licencia_categoria} />
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      key: 'acceso',
      header: t('externosAcceso.table.acceso'),
      width: 'w-28',
      cell: (e) =>
        e.tiene_acceso ? (
          <Tag tone="neutral">{t('externosAcceso.activo')}</Tag>
        ) : (
          <Tag tone="amber">{t('externosAcceso.invitado')}</Tag>
        ),
    },
  ]

  return (
    <div className="space-y-4" data-testid="externos-acceso-section">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 text-foreground">
          <KairosMark />
          <h2 className="font-heading text-lg">{t('externosAcceso.title')}</h2>
          <HelpIcon label={t('externosAcceso.tipLabel')} testId="externos-acceso-tip">
            {t('externosAcceso.tip')}
          </HelpIcon>
        </div>
        {/* Con la tabla vacía manda el primario del propio vacío (U7). */}
        {canManage && externos.length > 0 && (
          <Button size="sm" data-testid="externos-acceso-alta" onClick={() => setCreating(true)}>
            {t('externosAcceso.alta')}
          </Button>
        )}
      </div>

      <DataTable
        columns={columns}
        rows={externos}
        rowKey={(e) => e.id}
        emptyState={
          <ListEmptyState
            title={t('externosAcceso.empty')}
            action={
              <div className="flex flex-col items-center gap-3">
                {canManage && (
                  <Button size="sm" data-testid="externos-acceso-alta-vacio" onClick={() => setCreating(true)}>
                    {t('externosAcceso.alta')}
                  </Button>
                )}
                <Link
                  to={`/businesses/${businessId}/sgsst#std-1.1.1`}
                  className="text-sm font-medium text-primary hover:underline"
                  data-testid="externos-acceso-designar-link">
                  {t('externosAcceso.emptyCta')}
                </Link>
              </div>
            }
          />
        }
      />

      <InvitarExternoDialog businessId={businessId} open={creating} onOpenChange={setCreating} />
    </div>
  )
}
